import React from 'react';
import Box from '@mui/material/Box';

export function isFourSlotPosition(value) {
  return value?.kind === 'position_state' && Number(value.columns) === 4 && Number(value.rows || 1) === 1;
}

export function isPositionLine(value) {
  return value?.kind === 'position_state' && [4, 5].includes(Number(value.columns)) && Number(value.rows || 1) === 1;
}

export function isFourSlotPrompt(prompt) {
  const items = prompt?.items || [];
  return prompt?.layout === 'visual_flow' && items.length >= 2
    && items.some(isFourSlotPosition)
    && items.every((item) => isFourSlotPosition(item) || item?.kind === 'empty');
}

export function isPositionLinePrompt(prompt) {
  const items = prompt?.items || [];
  return prompt?.layout === 'visual_flow' && items.length >= 2 && items.some(isPositionLine)
    && items.every((item) => isPositionLine(item) || item?.kind === 'empty')
    && new Set(items.filter(isPositionLine).map((item) => Number(item.columns))).size === 1;
}

export function PositionLineValue({ value, variant }) {
  const missing = value?.kind === 'empty';
  const choice = variant === 'choice';
  const columns = Number(value?.columns) === 5 ? 5 : 4;
  const pitch = columns === 5 ? 21.2 : 27;
  const slotWidth = columns === 5 ? 20 : 25;
  return (
    <Box component="span" role="img" aria-label={value?.label || 'noktanın konumu'}
      data-testid="position-line-chip" data-position-variant={choice ? 'choice' : 'reference'}
      data-active-position={missing ? undefined : value.position}
      sx={{ display: 'inline-block', flex: '0 0 auto', width: choice ? { xs: 108, sm: 144, md: 160 } : '100%',
        maxWidth: choice ? '100%' : { xs: 88, sm: 104, md: 124 }, verticalAlign: 'middle',
        '@media (max-width: 389px)': choice ? { width: 96 } : {},
      }}
    >
      <Box component="svg" viewBox="0 0 120 80" aria-hidden="true" sx={{ display: 'block', width: '100%', height: 'auto' }}>
        {missing ? <text x="60" y="55" textAnchor="middle" fill="#6d45df" fontSize="44" fontWeight="900">?</text> : Array.from({ length: columns }, (_, index) => (
          <React.Fragment key={index}>
            <rect data-testid="position-line-slot" data-position-slot={index + 1} x={7 + pitch * index} y="24" width={slotWidth} height="32" rx="6" fill={index + 1 === value.position ? '#ede9fe' : '#faf8ff'} stroke="#c4b5fd" strokeWidth="1.5" />
            {index + 1 === value.position ? <circle data-testid="position-active-dot" cx={7 + slotWidth / 2 + pitch * index} cy="40" r={columns === 5 ? 7 : 9} fill="#312e81" /> : null}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}

export function PositionStepPrompt({ prompt, renderItem }) {
  const count = prompt.items.length;
  const mobileColumns = Math.min(3, count);
  const wideColumns = count <= 5 ? count : Math.ceil(count / 2);
  const boardColumns = Math.min(8, count);
  const rowEnd = (columns) => `& > [data-position-step]:nth-of-type(${columns}n) [data-position-connector]`;
  return (
    <Box data-testid="position-line-prompt" sx={{ width: '100%', minWidth: 0, maxWidth: 1100, display: 'grid', gap: 1 }}>
      {prompt.rule_hint ? <Box data-testid="position-rule-hint" sx={{ bgcolor: '#f5f0ff', color: '#5b21b6', borderRadius: 2,
        px: 1, py: 0.7, textAlign: 'center', fontSize: { xs: 15, md: 20 }, fontWeight: 800, lineHeight: 1.3,
      }}>{prompt.rule_hint}</Box> : null}
      <Box data-testid="position-steps" sx={{ display: 'grid', minWidth: 0, gap: '12px',
        gridTemplateColumns: `repeat(${mobileColumns}, minmax(0, 1fr))`,
        [rowEnd(mobileColumns)]: { visibility: 'hidden' },
        '@media (min-width: 390px)': {
          gridTemplateColumns: `repeat(${wideColumns}, minmax(0, 1fr))`,
          [rowEnd(mobileColumns)]: { visibility: 'visible' }, [rowEnd(wideColumns)]: { visibility: 'hidden' },
        },
        '@media (min-width: 900px)': {
          gridTemplateColumns: `repeat(${boardColumns}, minmax(0, 1fr))`,
          [rowEnd(mobileColumns)]: { visibility: 'visible' }, [rowEnd(wideColumns)]: { visibility: 'visible' },
          [rowEnd(boardColumns)]: { visibility: 'hidden' },
        },
      }}>
        {prompt.items.map((item, index) => (
          <Box key={index} data-position-step={index + 1} sx={{ position: 'relative', minWidth: 0, display: 'grid',
            justifyItems: 'center', alignContent: 'start', gap: 0.35,
          }}>
            {renderItem(item, index)}
            <Box component="span" sx={{ color: '#6d45df', fontSize: { xs: 12, md: 15 }, fontWeight: 900 }}>{index + 1}. adım</Box>
            {index + 1 < count && prompt.connector ? <Box component="span" data-position-connector aria-hidden="true"
              sx={{ position: 'absolute', right: -12, width: 12, top: 'calc(50% - 15px)', color: '#7c3aed', fontSize: 16, fontWeight: 900 }}
            >{prompt.connector}</Box> : null}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
